import React, { useState, useEffect } from "react";

const CreditCardCalculator = () => {
  const [outstandingBalance, setOutstandingBalance] = useState(50000);
  const [interestRate, setInterestRate] = useState(36);
  const [monthlyPayment, setMonthlyPayment] = useState(5000);
  const [paymentType, setPaymentType] = useState("fixed");
  const [minimumPaymentPercent, setMinimumPaymentPercent] = useState(5);
  const [annualFee, setAnnualFee] = useState(1000);
  const [result, setResult] = useState({
    monthsToPayoff: "0",
    totalPayment: "0.00",
    totalInterest: "0.00",
    effectiveAPR: "0.00",
    monthlyInterest: "0.00",
    payoffDate: "",
    avgMonthlyPayment: "0.00",
  });

  // Credit Card Interest Calculation Function
  const calculateCreditCard = () => {
    const balance = parseFloat(outstandingBalance);
    const annualRate = parseFloat(interestRate) / 100;
    const monthlyRate = annualRate / 12;
    let payment = parseFloat(monthlyPayment);
    const minPaymentPct = parseFloat(minimumPaymentPercent) / 100;
    const fee = parseFloat(annualFee);

    if (isNaN(balance) || isNaN(annualRate) || balance <= 0) return;

    let remainingBalance = balance;
    let totalInterestPaid = 0;
    let totalPaymentMade = 0;
    let months = 0;
    const maxMonths = 600; // 50 years safety limit

    // If minimum payment mode, calculate based on percentage
    if (paymentType === "minimum") {
      payment = Math.max(balance * minPaymentPct, 500); // Minimum ₹500
    }

    // Check if payment can cover monthly interest
    const monthlyInterestAmount = balance * monthlyRate;
    if (payment <= monthlyInterestAmount) {
      setResult({
        monthsToPayoff: "Never",
        totalPayment: "∞",
        totalInterest: "∞",
        effectiveAPR: interestRate.toFixed(2),
        monthlyInterest: monthlyInterestAmount.toFixed(2),
        payoffDate: "Never - Payment too low",
        avgMonthlyPayment: payment.toFixed(2),
      });
      return;
    }

    // Calculate payoff
    while (remainingBalance > 0 && months < maxMonths) {
      const interestCharge = remainingBalance * monthlyRate;
      totalInterestPaid += interestCharge;
      
      // Recalculate minimum payment each month if in minimum mode
      let currentPayment = payment;
      if (paymentType === "minimum") {
        currentPayment = Math.max(remainingBalance * minPaymentPct, 500);
        currentPayment = Math.min(currentPayment, remainingBalance + interestCharge);
      }
      
      const principalPayment = Math.min(currentPayment - interestCharge, remainingBalance);
      totalPaymentMade += currentPayment;
      remainingBalance -= principalPayment;
      months++;
      
      if (remainingBalance < 0.01) remainingBalance = 0;
    }

    // Add annual fee to total cost (prorated)
    const years = months / 12;
    const totalFees = fee * years;
    totalPaymentMade += totalFees;
    
    const effectiveAPR = ((totalInterestPaid + totalFees) / balance / years) * 100;
    const avgPayment = totalPaymentMade / months;

    // Calculate payoff date
    const today = new Date();
    const payoffDate = new Date(today.setMonth(today.getMonth() + months));
    const payoffDateStr = payoffDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

    setResult({
      monthsToPayoff: months.toString(),
      totalPayment: totalPaymentMade.toFixed(2),
      totalInterest: totalInterestPaid.toFixed(2),
      effectiveAPR: effectiveAPR.toFixed(2),
      monthlyInterest: (balance * monthlyRate).toFixed(2),
      payoffDate: payoffDateStr,
      avgMonthlyPayment: avgPayment.toFixed(2),
    });
  };

  useEffect(() => {
    calculateCreditCard();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
          </div>
          <h1>Credit Card Interest Calculator</h1>
          <p>
            Calculate how long it will take to pay off your credit card debt and see how much interest you'll pay
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                Credit Card Details
              </h2>

              {/* Outstanding Balance */}
              <div className="input-group">
                <label>Outstanding Balance</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={outstandingBalance}
                    onChange={(e) => setOutstandingBalance(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[25000, 50000, 100000, 200000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOutstandingBalance(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate (APR)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="36"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[24, 30, 36, 42].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Type */}
              <div className="input-group">
                <label>Payment Strategy</label>
                <div className="quick-select-group">
                  {[
                    { value: "fixed", label: "Fixed Amount" },
                    { value: "minimum", label: "Minimum Payment" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPaymentType(type.value)}
                      className={`quick-select-btn ${paymentType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Payment or Minimum Percentage */}
              {paymentType === "fixed" ? (
                <div className="input-group">
                  <label>Monthly Payment Amount</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      value={monthlyPayment}
                      onChange={(e) => setMonthlyPayment(e.target.value)}
                      className="calc-input"
                      placeholder="5000"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[2000, 5000, 10000, 15000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setMonthlyPayment(amount)}
                        className="quick-select-btn success"
                      >
                        ₹{(amount / 1000).toFixed(0)}K
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="input-group">
                  <label>Minimum Payment Percentage</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      value={minimumPaymentPercent}
                      onChange={(e) => setMinimumPaymentPercent(e.target.value)}
                      className="calc-input"
                      placeholder="5"
                      step="0.5"
                    />
                    <span className="input-suffix">%</span>
                  </div>
                  <div className="quick-select-group">
                    {[3, 5, 7, 10].map((pct) => (
                      <button
                        key={pct}
                        onClick={() => setMinimumPaymentPercent(pct)}
                        className="quick-select-btn success"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Annual Fee */}
              <div className="input-group">
                <label>Annual Card Fee</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={annualFee}
                    onChange={(e) => setAnnualFee(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 500, 1000, 2500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setAnnualFee(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${amount}`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateCreditCard} className="calculate-btn">
                Calculate Payoff
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Payoff Analysis
              </h2>

              <div className="result-card">
                {/* Time to Payoff - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Time to Pay Off Debt</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.monthsToPayoff === "Never" ? "Never" : `${result.monthsToPayoff} months`}
                  </div>
                  <div className="result-subtext">
                    {result.monthsToPayoff === "Never" ? "Increase your payment!" : `Debt free by ${result.payoffDate}`}
                  </div>
                </div>

                {/* Total Interest */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Interest Paid</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{result.totalInterest === "∞" ? "∞" : Number(result.totalInterest).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {interestRate}% APR
                  </div>
                </div>

                {/* Total Payment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount to Pay</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{result.totalPayment === "∞" ? "∞" : Number(result.totalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Principal + Interest + Fees
                  </div>
                </div>

                {/* Monthly Interest */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Interest Charge</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyInterest).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    First month interest
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Payment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Principal Amount</span>
                      <span className="breakdown-percentage">
                        ₹{Number(outstandingBalance).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: result.totalPayment === "∞" ? "100%" : `${(parseFloat(outstandingBalance) / parseFloat(result.totalPayment)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest + Fees</span>
                      <span className="breakdown-percentage">
                        ₹{result.totalInterest === "∞" ? "∞" : (parseFloat(result.totalPayment) - parseFloat(outstandingBalance)).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: result.totalPayment === "∞" ? "0%" : `${((parseFloat(result.totalPayment) - parseFloat(outstandingBalance)) / parseFloat(result.totalPayment)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Credit Card Interest</h3>
              <p>
                Credit card interest is calculated on your outstanding balance and compounds monthly, making it one 
                of the most expensive forms of debt. In India, credit card APRs typically range from 24% to 48% per 
                annum (2-4% per month). When you only pay the minimum amount due (usually 5% of outstanding balance), 
                most of your payment goes toward interest rather than reducing the principal. This calculator helps 
                you understand how long it will take to become debt-free and how much extra you'll pay in interest. 
                The key to minimizing interest charges is paying more than the minimum and paying on time. Even small 
                increases in monthly payments can significantly reduce your payoff time and total interest paid.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Credit Card Interest Formula:</h4>
            <div className="formula-display">
              Monthly Interest = Outstanding Balance × (Annual Rate ÷ 12)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Outstanding Balance</div>
                <div className="formula-param-value">Current debt on your card</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Annual Rate (APR)</div>
                <div className="formula-param-value">Yearly interest percentage</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Monthly Rate</div>
                <div className="formula-param-value">APR divided by 12 months</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Interest</div>
                <div className="formula-param-value">Sum of all monthly interest charges</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Why is only paying the minimum payment a bad idea?</h5>
              <p>
                Paying only the minimum amount (typically 5% of outstanding balance) extends your debt repayment 
                dramatically and increases total interest paid significantly. Most of your minimum payment goes 
                toward interest charges, with very little reducing the principal. For example, a ₹50,000 balance 
                at 36% APR with 5% minimum payments takes over 10 years to pay off and costs over ₹80,000 in 
                interest alone. The same debt with ₹5,000 monthly payments is cleared in 12 months with just 
                ₹9,000 in interest. Banks profit from minimum payments as you remain in debt longer. Always pay 
                more than the minimum, even if it's just ₹500-1,000 extra monthly. This dramatically reduces 
                payoff time and saves tens of thousands in interest charges.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is credit card interest calculated in India?</h5>
              <p>
                Indian credit cards charge interest on the daily outstanding balance using the Daily Reducing Balance 
                method. The annual percentage rate (APR) is divided by 365 to get the daily rate, then multiplied by 
                your daily balance. Interest is compounded monthly and charged if you don't pay the full statement 
                balance by the due date. There's usually a 45-50 day interest-free period from purchase to payment 
                due date, but this only applies if you pay the entire previous balance. Once you carry a balance, 
                new purchases immediately start accruing interest. Late payment penalties (typically ₹500-1,500) and 
                over-limit charges add to your costs. Always read your card's terms to understand exact calculation methods.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I consider a balance transfer to reduce credit card debt?</h5>
              <p>
                Balance transfer can be beneficial if you transfer high-interest card debt to a card with 0% or 
                lower promotional interest rate (typically 6-12 months). This lets you pay down principal faster 
                without interest accumulation. Only transfer if you commit to aggressive repayment and stop using credit cards. 
                Alternatively, consider personal loans at 10-15% APR to pay off 36%+ card debt, or negotiate with your 
                current bank for lower rates if you have good payment history. Balance transfers work best for disciplined 
                borrowers who have a clear payoff plan and won't accumulate new debt.
              </p>
            </div>

            <div className="faq-item">
              <h5>How does credit card debt affect my credit score?</h5>
              <p>
                Credit card debt impacts your CIBIL score through multiple factors. High credit utilization (using 
                more than 30% of your credit limit) significantly lowers your score, as it suggests financial stress. 
                Missing payments or paying late devastates your score and stays on record for 7 years. Making only 
                minimum payments doesn't hurt your score directly but keeps utilization high and debt persistent. 
                Maxed-out cards or over-limit balances severely damage scores. Maintain utilization below 30%, never miss payments, and work toward eliminating 
                balances to optimize your credit score for future borrowing at favorable rates.
              </p>
            </div>

            <div className="faq-item">
              <h5>What should I do if I can't afford my credit card payments?</h5>
              <p>
                If struggling with payments, act immediately before missing due dates. Contact your credit card bank's 
                customer service to explain your situation - many offer hardship programs with reduced interest rates, 
                waived fees, or modified payment plans. Consider converting outstanding balance to EMI at lower interest 
                (18-24% vs 36%+), though this has processing fees. Prioritize paying at least minimum to avoid late 
                fees and credit score damage.Don't ignore the 
                problem - unpaid debt leads to legal notices, collection agency involvement, and long-term credit damage. 
                As a last resort, credit counseling services can negotiate with banks on your behalf.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CreditCardCalculator;